import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField, Box, Typography,  Grid, 
  Card, CardContent, CardHeader, InputAdornment
} from '@mui/material';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

import { useWebSocket } from '../WebSocketContext';
import { isActionEnabled, buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionsValidateReservation = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  // Prepopulated values
  const DEFAULT_TAG = 'reservation_validation';
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const initialSettings = useRef({});
  const { ws, subscribeToMessages } = useWebSocket();
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [id, setId] = useState(settingId || 0);
  const [tag, setTag] = useState(DEFAULT_TAG);
  const [conditions, setConditions] = useState([]);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const tagRef = useRef(null);

  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    
    if (ws && settingId && !isTemporaryId) {
      ws.send(JSON.stringify(buildGetExportSettingsPayload(settingId, caseid)));
    } else if (isTemporaryId) {
      setId(settingId);
      setIsEnabled(false);
      setTag(DEFAULT_TAG);
      setConditions([]);
      
      initialSettings.current = {
        id: settingId,
        enable: false,
        tag: DEFAULT_TAG,
        useConditions: true,
        conditions: [],
        data: { name: 'Validate Reservation' }
      };
      // Don't set isSaveDisabled here - let updateSaveDisabledState handle it
    }
  }, [ws, caseid, settingId]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings' && parsedMessage.data.type === 'validatereservation') {
          console.log('Received export settings:', parsedMessage.data);
          const { id: newId, enable, customData } = parsedMessage.data;
          setId(newId);
          setIsEnabled(isActionEnabled(enable));
          
          if (customData.tag !== undefined) setTag(customData.tag);
          if (customData?.conditions !== undefined) setConditions(customData.conditions);

          initialSettings.current = {
            id: newId,
            enable: isActionEnabled(enable),
            tag: customData.tag || DEFAULT_TAG,
            useConditions: customData?.useConditions || false,
            conditions: customData?.conditions || []
          };
          setIsSaveDisabled(true);
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === 'validatereservation') {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            
            setId(savedId);
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              tag,
              useConditions: true,
              conditions: [...conditions],
              data: savedCustomData || initialSettings.current.data || {}
            };
            
            if (onSaveStatusChange) { 
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
            
            setIsSaveDisabled(true);
          }
        } else if (parsedMessage.command === 'forceRun') {
          setForceRunLoading(false);
          setForceRunResult(parsedMessage.data);
          setPopUpOpen(true);
        } else if (parsedMessage.command === 'forceRunError') {
          setForceRunLoading(false);
          setForceRunResult({ error: parsedMessage.data });
          setPopUpOpen(true);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, isEnabled, tag, conditions, onSaveStatusChange, id]);

  const updateSaveDisabledState = useCallback(() => {
    // For new/temporary actions, check if all required fields are filled
    if (typeof id === 'string' && id.startsWith('temp_')) {
      const hasRequiredFields = tag && tag.trim() !== '';
      setIsSaveDisabled(!hasRequiredFields);
      
      const currentId = id;
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasRequiredFields);
      }
      return;
    }

    // For existing actions, compare with initial settings
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);

      const unchanged =
        isEnabled === initialSettings.current.enable &&
        tag === initialSettings.current.tag &&
        initialSettings.current.useConditions === true &&
        currentConditionsJSON === initialConditionsJSON;

      const hasChanges = !unchanged;
      
      const currentId = initialSettings.current.id || id;
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }
      setIsSaveDisabled(unchanged);
    }
  }, [isEnabled, tag, conditions, id, onSaveStatusChange]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [isEnabled, tag, conditions, updateSaveDisabledState]);

  const handleConditionsChange = useCallback((newConditions) => {
    setConditions(newConditions);
  }, []);

  const handleSave = () => {
    if (ws) {
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      
      const settingsData = {
        tag,
        useConditions: true,
        conditions
      };
      
      const saveData = {
        command: 'saveActionSettings',
        id: currentId,
        caseid,
        type: 'validatereservation',
        enable: isEnabled ? '1' : '0',
        interval: '0',
        data: settingsData
      };
      ws.send(JSON.stringify(saveData));
    }
  };

  const handleForceRun = () => {
    if (ws) {
      setForceRunLoading(true);
      const forceRunData = {
        command: 'forceRun',
        caseid,
        export_id: id
      };
      ws.send(JSON.stringify(forceRunData));
    }
  };

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'tag') {
      tagRef.current?.insertTag(value);
    }
    handleMainTagMenuClose();
  };

  const closePopUp = () => {
    setPopUpOpen(false);
    setForceRunResult(null);
  };

  return (
    <ActionSettingsLayout
      title="Validate Reservation"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={closePopUp}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      primaryColor="#f44336"
    >
        {/* Configuration Section */}
        <Card sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #f4433610',
          overflow: 'hidden',
        }}>
          <CardHeader
            title={
              <Typography sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box component="span" sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(244, 67, 54, 0.2)',
                }}>
                  CONFIG
                </Box>
                Reservation Validation Settings
              </Typography>
            }
            sx={{
              borderBottom: '1px solid #fff8f8',
              backgroundColor: '#fffafa',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              p: { xs: 1.5, sm: 2.5 },
            }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                  Output Tag/Variable Name
                </Typography>
                <TagEditor
                    caseid={caseid}
                  ref={tagRef}
                  value={tag}
                  onChange={setTag}
                  placeholder="The variable name to store the validation result"
                  singleLine
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'tag')}
                />
              </Grid>
            </Grid>

            {/* Information about the fixed prompt */}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#444' }}>
                🤖 AI Analysis Prompt (Fixed)
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                "Based on the content, return give me a json like this:<br/>
                [{'"anomaly_percent":"" # from 0 to 10", "anomaly_reason":"" #reason'}, ...]<br/>
                if anything is abnormal in the referral letter"
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                This prompt will automatically analyze the document content for anomalies and return a structured JSON response with anomaly percentage and reasons.
              </Typography>
            </Box>
          </CardContent>
        </Card>

      {/* Tags Dropdown */}
      <TagsDropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        setAnchorEl={handleMainTagMenuClose}
        tags={tags}
        handleTagSelect={handleTagSelect}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default ActionsValidateReservation;

